/**
 * Formatting utilities for staking-related values
 */

const GRAVITY_DECIMALS = 6;

/**
 * Format a token amount from base units to human-readable format
 * @param amount - Amount in base units (e.g., ugraviton)
 * @param decimals - Number of decimal places (default: 6 for Gravity)
 * @param displayDecimals - Number of decimals to display (default: 2)
 * @returns Formatted string
 */
export function formatTokenAmount(
  amount: string | number,
  decimals: number = GRAVITY_DECIMALS,
  displayDecimals: number = 2
): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '0';
  
  const value = num / Math.pow(10, decimals);
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: displayDecimals,
  });
}

/**
 * Format a token amount with the token symbol
 * @param amount - Amount in base units
 * @param symbol - Token symbol (default: GRAV)
 * @param decimals - Number of decimal places (default: 6)
 * @param displayDecimals - Number of decimals to display (default: 2)
 * @returns Formatted string with symbol
 */
export function formatTokenWithSymbol(
  amount: string | number,
  symbol: string = 'GRAV',
  decimals: number = GRAVITY_DECIMALS,
  displayDecimals: number = 2
): string {
  return `${formatTokenAmount(amount, decimals, displayDecimals)} ${symbol}`;
}

/**
 * Format a DecCoin amount (18 decimals) to human-readable format with token symbol
 * Cosmos DecCoin amounts have 18 decimal places and need to be converted to display format
 * @param amount - Amount as string (with 18 decimal places encoded)
 * @param symbol - Token symbol (default: GRAV)
 * @param displayDecimals - Number of decimals to display (default: 6)
 * @returns Formatted string with symbol
 */
export function formatDecCoin(
  amount: string | number,
  symbol: string = 'GRAV',
  displayDecimals: number = 6
): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num) || num === 0) return `0 ${symbol}`;
  
  // DecCoin amounts have 18 decimals, divide by 1e18 to get the actual token amount
  const value = num / 1e18;
  
  return `${value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: displayDecimals,
  })} ${symbol}`;
}

/**
 * Format APR percentage
 * @param apr - APR as decimal or percentage string
 * @param displayDecimals - Number of decimals to display (default: 2)
 * @returns Formatted percentage string
 */
export function formatAPR(apr: string | number, displayDecimals: number = 2): string {
  let num = typeof apr === 'string' ? parseFloat(apr) : apr;
  if (isNaN(num)) return '0.00%';
  
  // If the number is less than 1, assume it's a decimal (0.15 = 15%)
  if (num < 1) {
    num = num * 100;
  }
  
  return `${num.toFixed(displayDecimals)}%`;
}

/**
 * Format a percentage value
 * @param value - Value as decimal (0.5 = 50%)
 * @param displayDecimals - Number of decimals to display (default: 2)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: string | number, displayDecimals: number = 2): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0.00%';
  
  return `${(num * 100).toFixed(displayDecimals)}%`;
}

/**
 * Format commission rate
 * @param commission - Commission as a large decimal string (needs to be divided by 1e18 then multiplied by 100 for percentage)
 * @returns Formatted percentage string
 */
export function formatCommission(commission: string | undefined): string {
  if (commission === undefined || commission === null || commission === '') return 'N/A';
  const num = parseFloat(commission);
  if (isNaN(num)) return 'N/A';
  
  // Commission comes as a fraction that needs to be divided by 1e18 then multiplied by 100
  // Example: 100000000000000000 / 1e18 * 100 = 10%
  const percentage = (num / 1e18) * 100;
  return `${percentage.toFixed(2)}%`;
}

/**
 * Format a large number with abbreviations (K, M, B)
 * @param num - Number to format
 * @param decimals - Number of decimals to display (default: 1)
 * @returns Formatted string
 */
export function formatLargeNumber(num: string | number, decimals: number = 1): string {
  const value = typeof num === 'string' ? parseFloat(num) : num;
  if (isNaN(value)) return '0';
  
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(decimals)}B`;
  } else if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(decimals)}M`;
  } else if (value >= 1_000) {
    return `${(value / 1_000).toFixed(decimals)}K`;
  }
  
  return value.toFixed(decimals);
}

/**
 * Format voting power as a percentage of total
 * @param validatorTokens - Validator's tokens
 * @param totalBonded - Total bonded tokens
 * @param displayDecimals - Number of decimals to display (default: 2)
 * @returns Formatted percentage string
 */
export function formatVotingPower(
  validatorTokens: string,
  totalBonded: string,
  displayDecimals: number = 2
): string {
  const tokens = parseFloat(validatorTokens);
  const total = parseFloat(totalBonded);
  
  if (isNaN(tokens) || isNaN(total) || total === 0) return '0.00%';
  
  return formatPercentage(tokens / total, displayDecimals);
}

/**
 * Shorten an address for display
 * @param address - Full address
 * @param prefixLength - Number of characters to show at start (default: 10)
 * @param suffixLength - Number of characters to show at end (default: 6)
 * @returns Shortened address
 */
export function shortenAddress(
  address: string,
  prefixLength: number = 10,
  suffixLength: number = 6
): string {
  if (address.length <= prefixLength + suffixLength) {
    return address;
  }
  
  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`;
}

/**
 * Format validator status number to readable string
 * @param status - Status number (0-3)
 * @returns Human-readable status
 */
export function formatValidatorStatus(status: number): string {
  switch (status) {
    case 0:
      return 'Unspecified';
    case 1:
      return 'Unbonded';
    case 2:
      return 'Unbonding';
    case 3:
      return 'Bonded';
    default:
      return 'Unknown';
  }
}

/**
 * Get badge color for validator status
 * @param status - Status number (0-3)
 * @returns Tailwind color class
 */
export function getValidatorStatusColor(status: number): string {
  switch (status) {
    case 3: // Bonded
      return 'bg-green-100 text-green-800';
    case 2: // Unbonding
      return 'bg-yellow-100 text-yellow-800';
    case 1: // Unbonded
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-red-100 text-red-800';
  }
}

/**
 * Format time remaining until a specific timestamp
 * @param timestamp - ISO timestamp string
 * @returns Human-readable time remaining
 */
export function formatTimeRemaining(timestamp: string): string {
  const now = new Date();
  const target = new Date(timestamp);
  const diff = target.getTime() - now.getTime();
  
  if (diff <= 0) return 'Completed';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) {
    return `${days}d ${hours}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
}
